import React, { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useForm } from 'react-hook-form';
import { toast } from 'sonner';
import { getAppSocket } from '../lib/wesSocket';
import { Button } from '../components/ui/button';
import { ButtonGroup } from '../components/ui/button-group';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Card, CardContent } from '../components/ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Textarea } from '../components/ui/textarea';
import { Checkbox } from '../components/ui/checkbox';
import PaginationFooter from '../components/PaginationFooter';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter
} from '../components/ui/dialog';
import {
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbLink,
  BreadcrumbList,
  BreadcrumbPage,
  BreadcrumbSeparator
} from '../components/ui/breadcrumb';
import { ShieldCheckIcon, PlusIcon, PencilIcon, TrashIcon, UsersIcon } from '@heroicons/react/24/outline';
import { Tip } from '../components/ui/tip';
import { confirmDialog } from '../components/ConfirmDialog';

interface Role {
  id: string;
  name: string;
  description: string;
  permissions: string[];
  isSystem: boolean;
  usersCount?: number;
}

interface RoleForm {
  name: string;
  description: string;
  permissions: string[];
}

const AVAILABLE_PERMISSIONS = [
  { id: 'dashboard', name: 'Ver Dashboard', category: 'General' },
  { id: 'clients', name: 'Clientes', category: 'Clientes' },
  { id: 'fleets', name: 'Flotas corporativas', category: 'Clientes' },
  { id: 'appointments', name: 'Turnos', category: 'Turnos' },
  { id: 'alerts', name: 'Ver Alertas del sistema', category: 'General' },
  { id: 'wes_operator', name: 'Operar WES', category: 'Taller' },
  { id: 'wes_general', name: 'Ver Taller General', category: 'WES' },
  { id: 'whatsapp', name: 'Chat / WhatsApp', category: 'Comercial' },
  { id: 'reopen_production', name: 'Volver OT a producción', category: 'Taller' },
  { id: 'reports', name: 'Reportes / Ventas / Caja', category: 'Comercial' },
  { id: 'invoices', name: 'Facturas y Notas de Crédito', category: 'Comercial' },
  { id: 'products', name: 'Productos y Servicios', category: 'Inventario' },
  { id: 'supplies', name: 'Insumos', category: 'Inventario' },
  { id: 'providers', name: 'Proveedores', category: 'Compras' },
  { id: 'finances', name: 'Finanzas (Caja, Cheques, Posición, Cuentas, Gastos, Compras)', category: 'Finanzas' },
  { id: 'current_accounts', name: 'Cuentas Corrientes', category: 'Finanzas' },
  { id: 'tasks_assign', name: 'Asignar Tareas', category: 'Tareas' },
  { id: 'request_supplies', name: 'Solicitar Insumos', category: 'Insumos' },
  { id: 'approve_supply_requests', name: 'Aprobar Pedidos Internos', category: 'Insumos' },
  { id: 'loyalty', name: 'Fidelizacion', category: 'Clientes' },
  { id: 'remitos', name: 'Diagnosticos / Remitos', category: 'Comercial' },
  { id: 'users', name: 'Gestionar Usuarios', category: 'Sistema' },
  { id: 'manage_settings', name: 'Gestionar Configuración', category: 'Sistema' },
  { id: 'supervisor', name: 'Supervisor (recibe avisos del personal)', category: 'Sistema' },
  { id: 'stock_alerts', name: 'Recibir alertas de stock negativo', category: 'Sistema' },
  { id: 'view_prices', name: 'Ver precios y montos', category: 'Sistema' },
  { id: 'create_appointments', name: 'Crear / editar / eliminar turnos', category: 'Turnos' },
];

const Roles: React.FC = () => {
  const navigate = useNavigate();
  const [roles, setRoles] = useState<Role[]>([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [selectedRole, setSelectedRole] = useState<Role | null>(null);
  const [selectedPermissions, setSelectedPermissions] = useState<string[]>([]);
  const [currentPage, setCurrentPage] = useState(1);
  const pageSize = 10;
  const { register, handleSubmit, reset, setValue, formState: { errors } } = useForm<RoleForm>();
  const [usersModalOpen, setUsersModalOpen] = useState(false);
  const [usersModalLoading, setUsersModalLoading] = useState(false);
  const [usersModalRole, setUsersModalRole] = useState<Role | null>(null);
  const [roleAllUsers, setRoleAllUsers] = useState<{ id: string; name: string; role: string }[]>([]);
  const [roleUserQuery, setRoleUserQuery] = useState('');
  const [roleUserBusyId, setRoleUserBusyId] = useState<string | null>(null);

  const fetchRoles = async (options?: { silent?: boolean }) => {
    const silent = options?.silent === true;
    if (!silent) setLoading(true);
    try {
      const response = await axios.get('/api/roles');
      setRoles(response.data.roles || []);
      setCurrentPage(1);
    } catch (error) {
      toast.error('Error al cargar los roles');
    } finally {
      if (!silent) setLoading(false);
    }
  };

  useEffect(() => {
    fetchRoles();

    const handleKeyPress = (e: KeyboardEvent) => {
      if (e.key === 'n' || e.key === 'N') {
        if (!showForm) {
          setSelectedRole(null);
          reset();
          setSelectedPermissions([]);
          setShowForm(true);
        }
      }
    };

    window.addEventListener('keydown', handleKeyPress);
    return () => window.removeEventListener('keydown', handleKeyPress);
  }, [showForm, reset]);

  const realtimeTimerRef = useRef<number | null>(null);
  const pendingRefreshRef = useRef(false);
  useEffect(() => {
    const socket = getAppSocket();

    const scheduleSilentRefresh = (payload?: any) => {
      const path = String(payload?.path || '');
      if (path && !(path.startsWith('/api/roles') || path.startsWith('/api/users'))) {
        return;
      }

      if (showForm) {
        pendingRefreshRef.current = true;
        return;
      }

      if (realtimeTimerRef.current) window.clearTimeout(realtimeTimerRef.current);
      realtimeTimerRef.current = window.setTimeout(() => {
        fetchRoles({ silent: true });
      }, 450);
    };

    socket.on('area:update', scheduleSilentRefresh);
    socket.on('connect', scheduleSilentRefresh);

    return () => {
      if (realtimeTimerRef.current) window.clearTimeout(realtimeTimerRef.current);
      socket.off('area:update', scheduleSilentRefresh);
      socket.off('connect', scheduleSilentRefresh);
    };
  }, [showForm]);

  useEffect(() => {
    if (showForm) return;
    if (!pendingRefreshRef.current) return;
    pendingRefreshRef.current = false;
    fetchRoles({ silent: true });
  }, [showForm]);

  const onSubmit = async (data: RoleForm) => {
    try {
      const payload = {
        ...data,
        permissions: selectedPermissions
      };

      if (selectedRole) {
        await axios.put(`/api/roles/${selectedRole.id}`, payload);
        toast.success('Rol actualizado');
      } else {
        await axios.post('/api/roles', payload);
        toast.success('Rol creado');
      }

      reset();
      setSelectedPermissions([]);
      setSelectedRole(null);
      setShowForm(false);
      fetchRoles();
    } catch (error: any) {
      toast.error(error.response?.data?.error || 'Error al guardar el rol');
    }
  };

  const handleEdit = (role: Role) => {
    setSelectedRole(role);
    setShowForm(true);
    setSelectedPermissions(role.permissions || []);
    reset({
      name: role.name,
      description: role.description,
      permissions: role.permissions
    });
  };

  const handleDelete = async (id: string) => {
    if (!await confirmDialog('Eliminar este rol? Los usuarios con este rol quedaran sin rol asignado.')) return;
    try {
      await axios.delete(`/api/roles/${id}`);
      toast.success('Rol eliminado');
      fetchRoles();
    } catch (error: any) {
      toast.error(error.response?.data?.error || 'Error al eliminar el rol');
    }
  };

  const handleCancel = () => {
    reset();
    setSelectedRole(null);
    setSelectedPermissions([]);
    setShowForm(false);
  };

  const handlePermissionToggle = (permissionId: string) => {
    setSelectedPermissions(prev =>
      prev.includes(permissionId)
        ? prev.filter(p => p !== permissionId)
        : [...prev, permissionId]
    );
  };

  const loadModalUsers = async () => {
    try {
      const response = await axios.get('/api/users');
      const list = (response.data.users || []).map((u: any) => ({
        id: u.id,
        name: `${u.firstName || ''} ${u.lastName || ''}`.trim() || u.username,
        role: String(u.role || '')
      }));
      setRoleAllUsers(list);
    } catch (error: any) {
      toast.error(error.response?.data?.error || 'No se pudieron cargar los usuarios');
    }
  };

  const openRoleUsers = async (role: Role) => {
    setUsersModalRole(role);
    setRoleAllUsers([]);
    setRoleUserQuery('');
    setUsersModalOpen(true);
    setUsersModalLoading(true);
    await loadModalUsers();
    setUsersModalLoading(false);
  };

  const assignRoleToUser = async (userId: string) => {
    if (!usersModalRole) return;
    setRoleUserBusyId(userId);
    try {
      await axios.put(`/api/users/${userId}`, { role: usersModalRole.name });
      await loadModalUsers();
      fetchRoles({ silent: true });
    } catch (error: any) {
      toast.error(error.response?.data?.error || 'No se pudo asignar el rol');
    } finally {
      setRoleUserBusyId(null);
    }
  };

  const removeRoleFromUser = async (userId: string) => {
    setRoleUserBusyId(userId);
    try {
      await axios.put(`/api/users/${userId}`, { role: '' });
      await loadModalUsers();
      fetchRoles({ silent: true });
    } catch (error: any) {
      toast.error(error.response?.data?.error || 'No se pudo quitar el rol');
    } finally {
      setRoleUserBusyId(null);
    }
  };

  const permissionsByCategory = AVAILABLE_PERMISSIONS.reduce((acc, perm) => {
    if (!acc[perm.category]) acc[perm.category] = [];
    acc[perm.category].push(perm);
    return acc;
  }, {} as Record<string, typeof AVAILABLE_PERMISSIONS>);

  return (
    <div className="p-6 space-y-6">
      <Breadcrumb>
        <BreadcrumbList>
          <BreadcrumbItem>
            <BreadcrumbLink asChild>
              <button onClick={() => navigate('/settings')} className="text-left">
                Configuración
              </button>
            </BreadcrumbLink>
          </BreadcrumbItem>
          <BreadcrumbSeparator />
          <BreadcrumbItem>
            <BreadcrumbPage>Roles y Permisos</BreadcrumbPage>
          </BreadcrumbItem>
        </BreadcrumbList>
      </Breadcrumb>

      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-foreground">Roles y Permisos</h1>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={() => navigate('/settings')} size="sm">
            Volver
          </Button>
          <Button
            onClick={() => { setSelectedRole(null); reset(); setSelectedPermissions([]); setShowForm(true); }}
            className="inline-flex items-center justify-center whitespace-nowrap text-sm font-bold h-8 rounded-lg gap-1.5 px-3 bg-black dark:bg-white text-white dark:text-black shadow-xs hover:bg-black/90 dark:hover:bg-white/90"
            size="sm"
          >
            <PlusIcon className="h-4 w-4 mr-2" />
            Nuevo rol
            <kbd className="ml-2 inline-flex items-center justify-center px-1 text-xs font-bold font-sans bg-gray-200 dark:bg-gray-800 text-gray-900 dark:text-white border-gray-300 dark:border-gray-700 rounded-sm border leading-none">
              N
            </kbd>
          </Button>
        </div>
      </div>

      <Dialog open={showForm} onOpenChange={setShowForm}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{selectedRole ? 'Editar rol' : 'Nuevo rol'}</DialogTitle>
          </DialogHeader>

          <form className="space-y-4" onSubmit={handleSubmit(onSubmit)}>
            <div className="grid gap-4 md:grid-cols-2">
              <div className="space-y-2">
                <Label className="text-sm font-medium">Nombre del rol *</Label>
                <Input
                  {...register('name', { required: 'Nombre requerido' })}
                  placeholder="ej: Gerente, Operador, etc."
                  disabled={selectedRole?.isSystem}
                />
                {errors.name && <p className="text-xs text-destructive">{errors.name.message}</p>}
              </div>
              <div className="space-y-2">
                <Label className="text-sm font-medium">Descripcion</Label>
                <Input
                  {...register('description')}
                  placeholder="Descripcion breve del rol"
                />
              </div>
            </div>

            <div className="space-y-3">
              <Label className="text-sm font-medium">Permisos</Label>
              <div className="space-y-4">
                {Object.entries(permissionsByCategory).map(([category, perms]) => (
                  <div key={category} className="space-y-2">
                    <h4 className="text-sm font-medium text-muted-foreground">{category}</h4>
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-2">
                      {perms.map(perm => (
                        <div key={perm.id} className="flex items-center space-x-2">
                          <Checkbox
                            id={perm.id}
                            checked={selectedPermissions.includes(perm.id)}
                            onCheckedChange={() => handlePermissionToggle(perm.id)}
                          />
                          <Label
                            htmlFor={perm.id}
                            className="text-sm font-normal cursor-pointer"
                          >
                            {perm.name}
                          </Label>
                        </div>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            </div>

            <DialogFooter>
              <Button variant="outline" type="button" onClick={handleCancel}>Cancelar</Button>
              <Button type="submit" className="bg-black text-white hover:bg-neutral-800 dark:bg-white dark:text-black dark:hover:bg-gray-100">
                {selectedRole ? 'Guardar cambios' : 'Crear rol'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      <Dialog open={usersModalOpen} onOpenChange={setUsersModalOpen}>
        <DialogContent className="max-w-lg max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="capitalize">Usuarios con rol "{usersModalRole?.name}"</DialogTitle>
          </DialogHeader>
          {usersModalLoading ? (
            <div className="flex items-center justify-center h-24">
              <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-primary-600"></div>
            </div>
          ) : (() => {
            const roleName = String(usersModalRole?.name || '').toLowerCase();
            const assigned = roleAllUsers.filter((u) => u.role.toLowerCase() === roleName);
            const q = roleUserQuery.trim().toLowerCase();
            const available = roleAllUsers
              .filter((u) => u.role.toLowerCase() !== roleName)
              .filter((u) => u.name.toLowerCase().includes(q));
            return (
              <div className="space-y-4">
                <div className="space-y-1.5">
                  <p className="text-xs font-semibold uppercase text-muted-foreground">Con este rol ({assigned.length})</p>
                  {assigned.length === 0 ? (
                    <p className="text-sm text-muted-foreground">Ningun usuario tiene este rol asignado.</p>
                  ) : (
                    <div className="space-y-1">
                      {assigned.map((u) => (
                        <div key={u.id} className="flex items-center justify-between rounded-lg border px-3 py-2 text-sm">
                          <span>{u.name}</span>
                          <Button
                            variant="ghost"
                            size="sm"
                            className="h-7 px-2 text-destructive hover:bg-destructive/10"
                            disabled={roleUserBusyId === u.id}
                            onClick={() => removeRoleFromUser(u.id)}
                          >
                            Quitar
                          </Button>
                        </div>
                      ))}
                    </div>
                  )}
                </div>

                <div className="space-y-1.5">
                  <p className="text-xs font-semibold uppercase text-muted-foreground">Agregar usuario</p>
                  <Input
                    placeholder="Buscar usuario..."
                    value={roleUserQuery}
                    onChange={(e) => setRoleUserQuery(e.target.value)}
                    className="h-8"
                  />
                  <div className="max-h-[200px] overflow-y-auto rounded-md border">
                    {available.length === 0 ? (
                      <p className="px-3 py-3 text-center text-sm text-muted-foreground">No hay usuarios para agregar</p>
                    ) : available.map((u) => (
                      <button
                        key={u.id}
                        type="button"
                        disabled={roleUserBusyId === u.id}
                        onClick={() => assignRoleToUser(u.id)}
                        className="flex w-full items-center justify-between px-3 py-2 text-left text-sm hover:bg-accent disabled:opacity-50"
                      >
                        <span>{u.name}{u.role ? <span className="text-muted-foreground capitalize"> · {u.role}</span> : <span className="text-muted-foreground"> · sin rol</span>}</span>
                        <span className="text-xs text-primary">Asignar</span>
                      </button>
                    ))}
                  </div>
                </div>
              </div>
            );
          })()}
          <DialogFooter>
            <Button type="button" onClick={() => setUsersModalOpen(false)}>Cerrar</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Card>
        <CardContent className="p-0">
          {loading ? (
            <div className="flex items-center justify-center h-32">
              <div className="animate-spin rounded-full h-16 w-16 border-b-2 border-primary-600"></div>
            </div>
          ) : roles.length === 0 ? (
            <div className="text-center py-12">
              <ShieldCheckIcon className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
              <p className="text-muted-foreground">No hay roles creados</p>
            </div>
          ) : (
            <div className="hidden md:block relative w-full overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow className="hover:bg-muted/50 border-b">
                    <TableHead className="text-foreground h-10 px-2 align-middle font-medium whitespace-nowrap text-left">Nombre</TableHead>
                    <TableHead className="text-foreground h-10 px-2 align-middle font-medium whitespace-nowrap text-left">Descripcion</TableHead>
                    <TableHead className="text-foreground h-10 px-2 align-middle font-medium whitespace-nowrap text-left">Permisos</TableHead>
                    <TableHead className="text-foreground h-10 px-2 align-middle font-medium whitespace-nowrap text-left">Usuarios</TableHead>
                    <TableHead className="text-foreground h-10 px-2 align-middle font-medium whitespace-nowrap text-left">Tipo</TableHead>
                    <TableHead className="text-foreground h-10 px-2 text-left align-middle font-medium whitespace-nowrap"></TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {roles
                    .slice((currentPage - 1) * pageSize, (currentPage - 1) * pageSize + pageSize)
                    .map((role) => (
                    <TableRow key={role.id} className="border-b hover:bg-muted/50">
                      <TableCell className="p-2 align-middle whitespace-nowrap text-left">
                        <div className="font-medium capitalize">{role.name}</div>
                      </TableCell>
                      <TableCell className="p-2 align-middle whitespace-nowrap text-left">
                        <div className="text-sm text-muted-foreground">{role.description || '-'}</div>
                      </TableCell>
                      <TableCell className="p-2 align-middle whitespace-nowrap text-left">
                        <Badge variant="secondary">{role.permissions?.length || 0} permisos</Badge>
                      </TableCell>
                      <TableCell className="p-2 align-middle whitespace-nowrap text-left">
                        <div className="text-sm">{role.usersCount || 0} usuarios</div>
                      </TableCell>
                      <TableCell className="p-2 align-middle whitespace-nowrap text-left">
                        {role.isSystem ? (
                          <Badge variant="default">Sistema</Badge>
                        ) : (
                          <Badge variant="secondary">Personalizado</Badge>
                        )}
                      </TableCell>
                      <TableCell className="p-2 align-middle whitespace-nowrap text-right">
                        <div className="flex items-center justify-end">
                          <ButtonGroup>
                            <Tip label="Ver usuarios con este rol">
                              <Button
                                variant="outline"
                                size="icon"
                                onClick={() => openRoleUsers(role)}
                                className="size-8"
                              >
                                <UsersIcon className="h-4 w-4" />
                              </Button>
                            </Tip>
                            <Tip label="Editar rol">
                              <Button
                                variant="outline"
                                size="icon"
                                onClick={() => handleEdit(role)}
                                className="size-8"
                              >
                                <PencilIcon className="h-4 w-4" />
                              </Button>
                            </Tip>
                            {role.name?.toLowerCase() !== 'admin' && (
                              <Tip label="Eliminar rol">
                                <Button
                                  variant="outline"
                                  size="icon"
                                  onClick={() => handleDelete(role.id)}
                                  className="size-8"
                                >
                                  <TrashIcon className="h-4 w-4" />
                                </Button>
                              </Tip>
                            )}
                          </ButtonGroup>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>

      <PaginationFooter
        currentPage={currentPage}
        totalPages={Math.max(1, Math.ceil(roles.length / pageSize))}
        totalResults={roles.length}
        startResult={(currentPage - 1) * pageSize + 1}
        onFirst={() => setCurrentPage(1)}
        onPrev={() => setCurrentPage((p) => Math.max(1, p - 1))}
        onNext={() => setCurrentPage((p) => Math.min(Math.max(1, Math.ceil(roles.length / pageSize)), p + 1))}
        onLast={() => setCurrentPage(Math.max(1, Math.ceil(roles.length / pageSize)))}
      />
    </div>
  );
};

export default Roles;








